import { Marked } from 'marked';
import { Theme } from './types';

// 使用本地 marked 实例，避免污染全局配置
const markedInstance = new Marked({
  gfm: true,
  breaks: true,
});

const renderer = new markedInstance.Renderer();
renderer.link = function ({ href, title, text }: { href: string; title?: string | null; text: string }) {
  const titleAttr = title ? ` title="${title}"` : '';
  return `<a href="${href}" target="_blank" rel="noopener noreferrer"${titleAttr}>${text}</a>`;
};
markedInstance.setOptions({ renderer });

export interface ChatModalConfig {
  title?: string;
  theme?: Theme;
  primaryColor?: string;
}

export class ChatModal {
  private overlay: HTMLDivElement | null = null;
  private body: HTMLDivElement | null = null;
  private reasoningEl: HTMLDivElement | null = null;
  private reasoningContent: HTMLDivElement | null = null;
  private aiContent: HTMLDivElement | null = null;
  private loadingEl: HTMLDivElement | null = null;
  private config: ChatModalConfig;
  private onClose: () => void;

  private fullContent: string = '';
  private fullReasoning: string = '';
  private isDestroyed: boolean = false;
  private bodyPaddingRight: string = '';
  private cancelFn: (() => void) | null = null;

  /** 获取浏览器滚动条宽度（缓存） */
  private static _scrollbarWidth: number = -1;
  private static getScrollbarWidth(): number {
    if (ChatModal._scrollbarWidth >= 0) return ChatModal._scrollbarWidth;
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll';
    document.body.appendChild(outer);
    const inner = document.createElement('div');
    outer.appendChild(inner);
    const width = outer.offsetWidth - inner.offsetWidth;
    outer.remove();
    ChatModal._scrollbarWidth = width;
    return width;
  }

  constructor(config: ChatModalConfig, onClose: () => void) {
    this.config = config;
    this.onClose = onClose;
  }

  /** 设置关闭前的取消回调（用于中断正在进行的流式对话） */
  public setCancelFn(fn: () => void): void {
    this.cancelFn = fn;
  }

  public create(): void {
    // 全屏遮罩
    this.overlay = document.createElement('div');
    this.overlay.className = 'chat-modal-overlay';
    this.overlay.setAttribute('data-theme', this.config.theme || 'light');

    // 居中弹窗容器
    const container = document.createElement('div');
    container.className = 'chat-modal-container';

    // 头部
    const header = document.createElement('div');
    header.className = 'chat-modal-header';

    const title = document.createElement('span');
    title.className = 'chat-modal-title';
    title.textContent = this.config.title || 'AI 对话';

    const closeBtn = document.createElement('button');
    closeBtn.className = 'chat-modal-close-btn';
    closeBtn.innerHTML = '&#10005;';
    closeBtn.title = '关闭';
    closeBtn.addEventListener('click', () => this.close());

    header.appendChild(title);
    header.appendChild(closeBtn);

    // 主体消息区域
    this.body = document.createElement('div');
    this.body.className = 'chat-modal-body';

    // 加载指示器（弹窗打开后追加到用户消息下方）
    this.loadingEl = document.createElement('div');
    this.loadingEl.className = 'chat-modal-loading';
    this.loadingEl.innerHTML = `
      <div class="chat-modal-spinner"></div>
      <span>正在思考中...</span>
    `;

    // 底部 AI 生成提示
    const disclaimer = document.createElement('div');
    disclaimer.className = 'chat-modal-disclaimer';
    disclaimer.textContent = '内容由AI生成，请谨慎甄别';

    container.appendChild(header);
    container.appendChild(this.body);
    container.appendChild(disclaimer);
    this.overlay.appendChild(container);

    document.body.appendChild(this.overlay);
  }

  /** 显示用户消息 */
  public setUserMessage(text: string): void {
    if (!this.body || this.isDestroyed) return;
    const el = document.createElement('div');
    el.className = 'chat-modal-message chat-modal-message--user';
    el.textContent = text;
    this.body.appendChild(el);
  }

  /** 追加 AI 回复内容和思考过程（增量 + 最终校正） */
  public appendContent(content: string, reasoning?: string, isComplete?: boolean): void {
    if (!this.body || this.isDestroyed) return;

    // 移除 loading
    if (this.loadingEl && this.loadingEl.parentNode) {
      this.loadingEl.remove();
    }

    // 更新思考过程（isComplete → 替换权威版本，否则 → 增量追加）
    if (reasoning || (isComplete && reasoning !== undefined)) {
      if (isComplete && reasoning) {
        // 仅在权威版本有实际内容时才覆盖，避免空字符串清空累积数据
        this.fullReasoning = reasoning;
      } else if (!isComplete) {
        this.fullReasoning += reasoning || '';
      }
      if (!this.reasoningEl) {
        this.reasoningEl = document.createElement('div');
        this.reasoningEl.className = 'chat-modal-reasoning';
        const label = document.createElement('div');
        label.className = 'chat-modal-reasoning-label';
        label.innerHTML = '<span class="chat-modal-reasoning-arrow">&#9660;</span> 思考过程';
        label.addEventListener('click', () => {
          this.reasoningEl!.classList.toggle('collapsed');
        });
        this.reasoningEl.appendChild(label);
        this.reasoningContent = document.createElement('div');
        this.reasoningContent.className = 'chat-modal-reasoning-content';
        this.reasoningEl.appendChild(this.reasoningContent);
        this.body!.appendChild(this.reasoningEl);
      }
      this.reasoningContent!.textContent = this.fullReasoning;
      // 思考过程更新时也自动滚动
      this.body!.scrollTop = this.body!.scrollHeight;
    }

    // 更新正文（isComplete → 替换权威版本，否则 → 增量追加）
    if (content || (isComplete && content !== undefined)) {
      if (isComplete && content) {
        // 仅在权威版本有实际内容时才覆盖，避免空字符串清空累积数据
        this.fullContent = content;
      } else if (!isComplete) {
        this.fullContent += content || '';
      }
      if (!this.aiContent) {
        const wrapper = document.createElement('div');
        wrapper.className = 'chat-modal-message chat-modal-message--ai';
        this.aiContent = document.createElement('div');
        this.aiContent.className = 'chat-modal-ai-content';
        wrapper.appendChild(this.aiContent);
        this.body.appendChild(wrapper);
      }
      this.aiContent.innerHTML = this.renderContent(this.fullContent);
    }

    // 自动滚动到底部
    this.body.scrollTop = this.body.scrollHeight;
  }

  /** 使用本地 marked 实例渲染 markdown，避免污染全局配置 */
  private renderContent(text: string): string {
    return markedInstance.parse(text) as string;
  }

  /** 在消息区展示错误信息 */
  public showError(message: string): void {
    if (!this.body || this.isDestroyed) return;

    // 移除 loading
    if (this.loadingEl && this.loadingEl.parentNode) {
      this.loadingEl.remove();
    }

    const el = document.createElement('div');
    el.className = 'chat-modal-message chat-modal-message--error';
    el.textContent = `${message}，请联系系统管理员或稍后再试`;
    this.body.appendChild(el);
    this.body.scrollTop = this.body.scrollHeight;
  }

  public show(): void {
    if (!this.overlay || this.isDestroyed) return;

    // 补偿滚动条宽度，避免 body overflow:hidden 时页面抖动
    const scrollbarWidth = ChatModal.getScrollbarWidth();
    if (scrollbarWidth > 0) {
      this.bodyPaddingRight = document.body.style.paddingRight || '';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.body.style.overflow = 'hidden';

    // 追加 loading 到用户消息下方
    if (this.loadingEl && !this.loadingEl.parentNode) {
      this.body!.appendChild(this.loadingEl);
    }

    // 触发动画
    requestAnimationFrame(() => {
      this.overlay?.classList.add('is-visible');
    });
  }

  public close(): void {
    if (!this.overlay || this.isDestroyed) return;

    // 流式对话未完成时，前端中断 fetch 请求
    if (this.cancelFn) {
      this.cancelFn();
    }

    this.overlay.classList.remove('is-visible');
    this.restoreBodyOverflow();
    // 动画结束后销毁
    setTimeout(() => {
      this.destroy();
      this.onClose();
    }, 250);
  }

  public destroy(): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
      this.body = null;
      this.reasoningEl = null;
      this.reasoningContent = null;
      this.aiContent = null;
      this.loadingEl = null;
    }
    this.restoreBodyOverflow();
  }

  /** 恢复 body 的 overflow 和 padding，避免页面抖动 */
  private restoreBodyOverflow(): void {
    document.body.style.overflow = '';
    if (this.bodyPaddingRight !== undefined) {
      document.body.style.paddingRight = this.bodyPaddingRight;
      this.bodyPaddingRight = '';
    }
  }
}
